import { useState, useCallback } from 'react';
import { TrainingApiService } from '../../../services/trainingApiService';
import {
  ModelMetrics,
  TrainedModelWithMetrics,
  ModelComparisonData,
  UseModelMetricsReturn
} from '../types';

/**
 * Hook for managing model metrics and comparison
 * Fetches metrics from evaluation results and enables model comparison
 */
export const useModelMetrics = (sessionId: string): UseModelMetricsReturn => {
  const [metricsMap, setMetricsMap] = useState<Map<string, ModelMetrics>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch metrics for a specific model
   * Metrics come from the comprehensive evaluation endpoint
   */
  const fetchMetricsForModel = useCallback(async (modelFilename: string): Promise<ModelMetrics | null> => {
    if (!sessionId) return null;

    // Check cache first
    if (metricsMap.has(modelFilename)) {
      return metricsMap.get(modelFilename) || null;
    }

    setLoading(true);
    setError(null);

    try {
      // Get comprehensive evaluation which contains metrics
      const response = await TrainingApiService.getComprehensiveEvaluation(sessionId);

      if (response.success && response.data) {
        // Extract metrics from evaluation data
        // Structure depends on backend response
        const evaluationData = response.data as {
          evaluation_results?: {
            mae?: number;
            mse?: number;
            rmse?: number;
            r2?: number;
            mape?: number;
          };
        };

        if (evaluationData.evaluation_results) {
          const metrics: ModelMetrics = {
            mae: evaluationData.evaluation_results.mae,
            mse: evaluationData.evaluation_results.mse,
            rmse: evaluationData.evaluation_results.rmse,
            r2: evaluationData.evaluation_results.r2,
            mape: evaluationData.evaluation_results.mape
          };

          // Update cache
          setMetricsMap(prev => {
            const newMap = new Map(prev);
            newMap.set(modelFilename, metrics);
            return newMap;
          });

          return metrics;
        }
      }

      return null;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch metrics';
      setError(errorMessage);
      console.error('Error fetching model metrics:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [sessionId, metricsMap]);

  /**
   * Compare multiple models by their metrics
   * Returns a structured comparison object
   */
  const compareModels = useCallback((filenames: string[]): ModelComparisonData => {
    const modelsWithMetrics: TrainedModelWithMetrics[] = filenames.map(filename => {
      const metrics = metricsMap.get(filename);
      return {
        filename,
        path: '',
        model_type: '',
        file_size: 0,
        file_size_mb: 0,
        modified_time: '',
        format: 'h5',
        metrics
      };
    });

    return {
      models: modelsWithMetrics,
      selectedModels: filenames,
      comparisonMetric: 'r2' // Default comparison metric
    };
  }, [metricsMap]);

  return {
    metricsMap,
    loading,
    error,
    fetchMetricsForModel,
    compareModels
  };
};
